import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface BreadcrumbNavItem {
  label: string;
  href?: string; // If provided, renders as a link; otherwise renders as current page
  icon?: React.ReactNode; // Optional icon displayed before the label
}

interface PageBreadcrumbProps {
  items: BreadcrumbNavItem[];
  className?: string;
  /** Show Media Orchestrator home icon at the start of breadcrumb */
  showHomeIcon?: boolean;
}

export const PageBreadcrumb: React.FC<PageBreadcrumbProps> = ({
  items,
  className,
  showHomeIcon = true
}) => {
  const navigate = useNavigate();

  // Navigate to Media Orchestrator home (parent app dashboard)
  const handleHomeClick = () => {
    // Navigate to the parent app's dashboard (Media Orchestrator home)
    window.location.href = '/dashboard';
  };

  return (
    <div className={className} style={{ minHeight: '40px', display: 'flex', alignItems: 'center' }}>
      <Breadcrumb>
        <BreadcrumbList>
          {/* Media Orchestrator Home Icon */}
          {showHomeIcon && (
            <>
              <BreadcrumbItem>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <BreadcrumbLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleHomeClick();
                        }}
                        className="text-muted-foreground hover:text-cyan-400 transition-colors flex items-center"
                      >
                        <img
                          src="/mo-menu-icon.png"
                          alt="Media Orchestrator"
                          className="h-4 w-4 object-contain"
                        />
                      </BreadcrumbLink>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Media Orchestrator Home</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </BreadcrumbItem>
              {items.length > 0 && <BreadcrumbSeparator />}
            </>
          )}

          {/* Remaining breadcrumb items */}
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            const isLink = !!item.href;

            return (
              <React.Fragment key={index}>
                <BreadcrumbItem>
                  {isLink ? (
                    <BreadcrumbLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(item.href!);
                      }}
                      className="text-muted-foreground hover:text-cyan-400 transition-colors flex items-center gap-1.5"
                    >
                      {item.icon}
                      {item.label}
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbPage className="truncate max-w-[300px] flex items-center gap-1.5" style={{ color: 'var(--pm-cyan-text)' }}>
                      {item.icon}
                      {item.label}
                    </BreadcrumbPage>
                  )}
                </BreadcrumbItem>
                {!isLast && <BreadcrumbSeparator />}
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
};
